import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  ClipboardCheck, 
  CheckCircle2, 
  XCircle,
  Clock,
  GraduationCap,
  Building2,
  Calendar,
  Filter,
  UserMinus,
  AlertTriangle
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ExportDropdown } from "../components/ui/export-dropdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function Approvals() {
  const { user } = useAuth();
  const [placementRequests, setPlacementRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [unassignDialog, setUnassignDialog] = useState({ open: false, request: null });
  const [unassigning, setUnassigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requestsRes = await api.get("/placement-requests");
      setPlacementRequests(requestsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    if (!unassignDialog.request) return;
    
    setUnassigning(true);
    try {
      // Update the placement request status back to pending or create a new endpoint
      await api.put(`/placement-requests/${unassignDialog.request.id}/unassign`);
      
      // Update local state
      setPlacementRequests(placementRequests.map(r => 
        r.id === unassignDialog.request.id 
          ? { ...r, status: "pending" } 
          : r
      ));
      
      toast.success(`${unassignDialog.request.student_name} has been unassigned from ${unassignDialog.request.clinical_site_name}`);
      setUnassignDialog({ open: false, request: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unassign student");
    } finally {
      setUnassigning(false);
    }
  };

  const getStatusBadge = (status, isRequest = false) => {
    const styles = {
      pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      accepted: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      confirmed: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      rejected: "bg-red-500/10 text-red-400 border-red-500/20",
      countered: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      denied: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    const label = status === "denied" ? "Rejected" : status;
    return <Badge className={styles[status] || "bg-slate-500/10 text-slate-400"}>{label}</Badge>;
  };

  // Only show placement requests (no direct placements)
  const getFilteredItems = () => {
    let items = placementRequests.map(r => ({
      ...r,
      type: "request",
      displayStatus: r.status
    }));
    
    // Filter by status
    if (filterStatus) {
      items = items.filter(item => item.status === filterStatus || item.displayStatus === filterStatus);
    }
    
    // Sort by created_at descending (most recent first)
    items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return items;
  };

  const filteredItems = getFilteredItems();

  // Stats counts
  const requestPendingCount = placementRequests.filter(r => r.status === "pending").length;
  const requestAcceptedCount = placementRequests.filter(r => r.status === "accepted").length;
  const requestCounteredCount = placementRequests.filter(r => r.status === "countered").length;
  const requestRejectedCount = placementRequests.filter(r => r.status === "denied" || r.status === "rejected").length;
  const requestConfirmedCount = placementRequests.filter(r => r.status === "confirmed").length;

  const canApprove = user?.role === "admin" || user?.role === "clinical_site";
  const canConfirm = user?.role === "admin" || user?.role === "university";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="approvals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Approval Queue</h1>
          <p className="text-slate-400 mt-1">
            Manage placement requests and approvals
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ExportDropdown 
            endpoint="/export/placements" 
            filename="placements"
            queryParams={filterStatus ? { status: filterStatus } : {}}
          />
          <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-slate-800/50 border-slate-700 text-slate-200" data-testid="filter-status-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#151A23] border-slate-700">
              <SelectItem value="all" className="text-slate-200 focus:bg-slate-800">All Status</SelectItem>
              <SelectItem value="pending" className="text-slate-200 focus:bg-slate-800">Pending</SelectItem>
              <SelectItem value="accepted" className="text-slate-200 focus:bg-slate-800">Accepted</SelectItem>
              <SelectItem value="confirmed" className="text-slate-200 focus:bg-slate-800">Confirmed</SelectItem>
              <SelectItem value="countered" className="text-slate-200 focus:bg-slate-800">Countered</SelectItem>
              <SelectItem value="rejected" className="text-slate-200 focus:bg-slate-800">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-colors ${filterStatus === "pending" ? "border-yellow-400/50" : ""}`}
          onClick={() => setFilterStatus(filterStatus === "pending" ? "" : "pending")}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-yellow-400/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Pending</p>
              <p className="text-2xl font-bold text-slate-100">{requestPendingCount}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-colors ${filterStatus === "accepted" ? "border-emerald-400/50" : ""}`}
          onClick={() => setFilterStatus(filterStatus === "accepted" ? "" : "accepted")}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-emerald-400/10 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Accepted</p>
              <p className="text-2xl font-bold text-slate-100">{requestAcceptedCount}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-colors ${filterStatus === "confirmed" ? "border-purple-400/50" : ""}`}
          onClick={() => setFilterStatus(filterStatus === "confirmed" ? "" : "confirmed")}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-400/10 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Confirmed</p>
              <p className="text-2xl font-bold text-slate-100">{requestConfirmedCount}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`bg-[#151A23] border-slate-700 cursor-pointer transition-colors ${filterStatus === "rejected" ? "border-red-400/50" : ""}`}
          onClick={() => setFilterStatus(filterStatus === "rejected" ? "" : "rejected")}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-red-400/10 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Rejected</p>
              <p className="text-2xl font-bold text-slate-100">{requestRejectedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Guide */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardContent className="p-4">
          <p className="text-slate-400 text-sm mb-4">Approval Workflow:</p>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-400/10 rounded-full flex items-center justify-center">
                <span className="text-yellow-400 text-sm font-bold">1</span>
              </div>
              <div>
                <span className="text-slate-300 text-sm">Pending</span>
                <p className="text-slate-500 text-xs">Request submitted</p>
              </div>
            </div>
            <div className="text-slate-600">→</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-400/10 rounded-full flex items-center justify-center">
                <span className="text-emerald-400 text-sm font-bold">2</span>
              </div>
              <div>
                <span className="text-slate-300 text-sm">Accepted</span>
                <p className="text-slate-500 text-xs">Clinical site approves</p>
              </div>
            </div>
            <div className="text-slate-600">→</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-400/10 rounded-full flex items-center justify-center">
                <span className="text-purple-400 text-sm font-bold">3</span>
              </div>
              <div>
                <span className="text-slate-300 text-sm">Confirmed</span>
                <p className="text-slate-500 text-xs">Placement finalized</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Request List */}
      {filteredItems.length === 0 ? (
        <Card className="bg-[#151A23] border-slate-700">
          <CardContent className="py-16 text-center">
            <ClipboardCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">No requests found</h3>
            <p className="text-slate-500">
              {filterStatus ? "Try a different filter" : "Placement requests will appear here"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((item) => (
            <Card 
              key={`request-${item.id}`} 
              className="bg-[#151A23] border-slate-700 hover:border-cyan-400/30 transition-colors"
              data-testid={`request-card-${item.id}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Student Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-100">{item.student_name}</h3>
                        <Badge className={item.type === "request" ? "bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs" : "bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs"}>
                          {item.type === "request" ? "Request" : "Placement"}
                        </Badge>
                      </div>
                      <p className="text-slate-500 text-sm">
                        {item.type === "request" ? "Placement request" : "Direct assignment"}
                      </p>
                    </div>
                  </div>

                  {/* Site/Rotation Info */}
                  <div className="flex-1 lg:mx-6">
                    <div className="p-3 bg-slate-800/30 rounded-lg space-y-2">
                      <div>
                        <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">
                          Clinical Site
                        </p>
                        <p className="text-slate-200 font-medium">
                          {item.clinical_site_name}
                        </p>
                      </div>
                      
                      {/* Rotation Type and Dates for Requests */}
                      {item.rotation_type && (
                        <div className="pt-2 border-t border-slate-700/50">
                          <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">
                            Rotation Type & Dates
                          </p>
                          <p className="text-cyan-400 text-sm font-medium">
                            {item.rotation_type}
                          </p>
                        </div>
                      )}
                      
                      {/* Program Information */}
                      {item.student_program && (
                        <div className="pt-2 border-t border-slate-700/50">
                          <p className="text-slate-400 text-xs uppercase tracking-wide mb-1">
                            Program
                          </p>
                          <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                            {item.student_program}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Status & Actions */}
                  <div className="flex flex-col items-end gap-3">
                    {getStatusBadge(item.status, item.type === "request")}
                    
                    <div className="flex gap-2">
                      {/* Actions for Placement Requests */}
                      {item.type === "request" && (
                        <>
                          {/* Clinical Site can accept/reject pending requests */}
                          {item.status === "pending" && canApprove && (
                            <>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.put(`/placement-requests/${item.id}/status`, { status: "accepted" });
                                    toast.success("Request accepted!");
                                    fetchData();
                                  } catch (error) {
                                    toast.error("Failed to accept request");
                                  }
                                }}
                                className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Accept
                              </Button>
                              <Button
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.put(`/placement-requests/${item.id}/status`, { status: "denied" });
                                    toast.success("Request denied");
                                    fetchData();
                                  } catch (error) {
                                    toast.error("Failed to deny request");
                                  }
                                }}
                                className="bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          
                          {/* University can confirm accepted requests */}
                          {item.status === "accepted" && canConfirm && (
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await api.post(`/placement-requests/${item.id}/confirm`);
                                  toast.success("Placement confirmed!");
                                  fetchData();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Failed to confirm placement");
                                }
                              }}
                              className="bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 border border-purple-500/20"
                            >
                              <ClipboardCheck className="w-4 h-4 mr-1" />
                              Confirm Placement
                            </Button>
                          )}
                          
                          {/* University and Clinical Site can unassign confirmed requests */}
                          {item.status === "confirmed" && (canConfirm || canApprove) && (
                            <Button
                              size="sm"
                              onClick={() => setUnassignDialog({ open: true, request: item })}
                              className="bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/20"
                            >
                              <UserMinus className="w-4 h-4 mr-1" />
                              Unassign
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
                  <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                  {item.short_id && <span>ID: {item.short_id}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Unassign Confirmation Dialog */}
      <AlertDialog open={unassignDialog.open} onOpenChange={(open) => !open && setUnassignDialog({ open: false, request: null })}>
        <AlertDialogContent className="bg-[#151A23] border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
              Unassign Student
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to unassign <span className="text-slate-200 font-medium">{unassignDialog.request?.student_name}</span> from{" "}
              <span className="text-slate-200 font-medium">{unassignDialog.request?.clinical_site_name}</span>?
              <br /><br />
              This will reset the placement request to pending status. The student will need to go through the approval process again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnassign}
              disabled={unassigning}
              className="bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30"
            >
              {unassigning ? "Unassigning..." : "Yes, Unassign Student"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
